// src/pages/assessment/AssessmentBuilder.tsx
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Snakbar from 'utils/Snackbar';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Paper,
  Stack,
  Checkbox,
  Tooltip,
  LinearProgress,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  alpha,
  useTheme,
  Alert
} from '@mui/material';
import { Add, Bag, Book, NoteText } from 'iconsax-reactjs';
import MainCard from 'components/MainCard';
import BackToggle from 'components/third-party/dropzone/BackToggle';
import { getQuestions } from 'services/questions.service';
import { bulkUpsert, getByAssessmentTemplateId } from 'services/questionBank.service';
import { getRubrics } from 'services/rubric.service';
import CustomTable from 'components/@extended/CustomTable';
import { useNavigate } from 'react-router-dom';
interface AssessmentData {
  _id: string;
  endDate: string;
  startDate: string;
  templateName: string;
  qualificationPack: {
    _id: string;
    qpCode: string;
    qpName: string;
    nsqfLevel: number;
  };
  batch: {
    _id: string;
    name: string;
    batchStartDate: string;
    batchEndDate: string;
  };
  nos: {
    _id: string;
    pcIds?: string[];
    nosCode: string;
    nosName: string;
    totalMarks: number;
  }[];
  pcs: {
    _id: string;
    pcCode: string;
    pcName: string;
    totalMarks: number;
    isCritical: boolean;
    type: any;
    questions?: Question[];
  }[];
}

interface Question {
  id: string;
  text: string;
  marks?: number;
  questionType?: string;
}

const getPcTypeLabel = (type: any): string => {
  if (!type) return '';
  if (typeof type === 'string') return type;
  if (Array.isArray(type)) {
    const titles = type.map((t) => (t && typeof t.title === 'string' ? t.title : '')).filter(Boolean);
    return titles.join(' / ');
  }
  if (typeof type === 'object' && typeof type.title === 'string') return type.title;
  return String(type);
};

// Normalize a PC type value to its main category used for filtering
// questions: 'theory' | 'practical' | ''.
const getPcMainType = (type: any): 'theory' | 'practical' | '' => {
  if (!type) return '';
  if (typeof type === 'string') {
    const t = type.toLowerCase();
    return t === 'theory' || t === 'practical' ? (t as 'theory' | 'practical') : '';
  }
  if (Array.isArray(type)) {
    const titles = type.map((t) => (t && typeof t.title === 'string' ? t.title.toLowerCase() : '')).filter(Boolean);
    if (titles.some((t) => t === 'task')) return 'practical';
    if (titles.some((t) => t === 'question' || t === 'viva')) return 'theory';
    return '';
  }
  return '';
};

// Map backend PC subtype titles to question categories used in the bank.
// 'question' / 'viva' -> 'theory', 'task' -> 'practical'.
const mapPcSubTypeToQuestionCategory = (title: string): 'theory' | 'practical' | null => {
  const t = title.toLowerCase();
  if (t === 'question' || t === 'viva') return 'theory';
  if (t === 'task') return 'practical';
  return null;
};

// Given a PC "type" field, determine which questionType values are allowed
// for that PC: 'question' | 'task' | 'viva'.
const getAllowedQuestionTypesForPc = (type: any): Array<'question' | 'task' | 'viva'> => {
  const set = new Set<'question' | 'task' | 'viva'>();

  if (!type) {
    // No restriction – allow all
    return ['question', 'task', 'viva'];
  }

  if (typeof type === 'string') {
    const t = type.toLowerCase();
    if (t === 'theory') {
      set.add('question');
      set.add('viva');
    } else if (t === 'practical') {
      set.add('task');
    } else if (t === 'question' || t === 'task' || t === 'viva') {
      set.add(t);
    }
  } else if (Array.isArray(type)) {
    type.forEach((entry) => {
      const title = entry && typeof entry.title === 'string' ? entry.title.toLowerCase() : '';
      if (!title) return;
      if (title === 'question') set.add('question');
      if (title === 'task') set.add('task');
      if (title === 'viva') set.add('viva');
    });
  }

  const result = Array.from(set);
  return result.length ? result : ['question', 'task', 'viva'];
};

const AssessmentQuestions = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { templateDetails } = location.state || {};

  const assessment: AssessmentData = templateDetails || {
    _id: '',
    endDate: '',
    startDate: '',
    templateName: '',
    qualificationPack: { _id: '', qpCode: '', qpName: '', nsqfLevel: 0 },
    batch: { _id: '', name: '', batchStartDate: '', batchEndDate: '' },
    nos: [],
    pcs: []
  };

  const sessionType = templateDetails?.sessionType ?? templateDetails?.type ?? null;
  const attemptNumber = templateDetails?.attemptNumber ?? 1;
  const performanceEvalLocked =
    Boolean(templateDetails?.resultsReleased) ||
    String(templateDetails?.sessionStatus ?? '').toLowerCase() === 'completed';
  const [pcs, setPcs] = useState(() =>
    assessment.pcs.map((pc) => ({
      ...pc,
      questions: pc.questions ?? ([] as Question[])
    }))
  );

  const [questions, setQuestions] = useState<any[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(true);
  const [questionBankList, setQuestionBankList] = useState<any[]>([]);
  const [rubricMaxMarkByRubricId, setRubricMaxMarkByRubricId] = useState<Record<string, number>>({});
  const [open, setOpen] = useState(false);
  const [currentPcIndex, setCurrentPcIndex] = useState(0);
  const [sorting, setSorting] = useState<any[]>([]);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasMergedBankRef = useRef(false);

  // Load all available questions (from backend without pagination)
  useEffect(() => {
    setQuestionsLoading(true);
    getQuestions({ limit: 0 })
      .then((res) => {
        const root = res?.data;
        const inner = root?.data;
        const list = Array.isArray(inner?.data) ? inner.data : Array.isArray(inner) ? inner : [];
        setQuestions(list || []);
      })
      .catch((err) => {
        console.error('Failed to load questions:', err);
        setQuestions([]);
      })
      .finally(() => setQuestionsLoading(false));
  }, []);

  // Load rubrics (from backend without pagination) and build rubricId -> maxMark map (max of levels[].scorePercent = total mark for that question)
  useEffect(() => {
    getRubrics({
      limit: 0,
      ...(assessment.batch?._id && assessment._id
        ? { batchId: String(assessment.batch._id), assessmentSessionId: String(assessment._id) }
        : {})
    })
      .then((res) => {
        const list = res?.data?.data?.data ?? res?.data?.data ?? res?.data ?? [];
        const rubrics = Array.isArray(list) ? list : [];
        const map: Record<string, number> = {};
        rubrics.forEach((r: any) => {
          const id = r?._id;
          if (!id || !Array.isArray(r?.levels) || r.levels.length === 0) return;
          const maxMark = Math.max(...r.levels.map((l: any) => Number(l?.scorePercent) || 0));
          map[id] = maxMark;
        });
        setRubricMaxMarkByRubricId(map);
      })
      .catch((err) => {
        console.error('Failed to load rubrics:', err);
        setRubricMaxMarkByRubricId({});
      });
  }, [assessment._id, assessment.batch?._id]);

  useEffect(() => {
    if (!assessment._id) return;
    hasMergedBankRef.current = false;
    getByAssessmentTemplateId(assessment._id)
      .then((res) => setQuestionBankList(res?.data?.data?.data || []))
      .catch((err) => console.error('Failed to load question bank:', err));
  }, [assessment]);

  /** Full QP NOS list for configuring the paper; student evaluation is scoped to failed NOS per student. */
  const displayNosList = assessment.nos || [];

  // Normalize pcId from question bank entry (handles populated object or raw id)
  const getPcIdFromEntry = (b: any) => (typeof b.pcId === 'object' && b.pcId != null ? b.pcId._id : b.pcId);

  /** Same max-mark logic as the question table: prefer embedded rubric.levels, then rubric id map, then bank fields. */
  const getQuestionMaxMarkForQuestion = (questionRow: any): number => {
    if (!questionRow || typeof questionRow !== 'object') return 1;
    const r = questionRow.rubric;
    if (r && typeof r === 'object' && Array.isArray((r as any).levels) && (r as any).levels.length > 0) {
      const maxFromLevels = Math.max(...(r as any).levels.map((l: any) => Number(l?.scorePercent) || 0), 0);
      if (maxFromLevels > 0) return maxFromLevels;
    }
    if (r) {
      const rubricId =
        typeof r === 'object' && r !== null && '_id' in r ? String((r as { _id: unknown })._id) : String(r);
      const fromMap = rubricMaxMarkByRubricId[rubricId];
      if (typeof fromMap === 'number' && fromMap > 0) return fromMap;
    }
    const directMax = Number(questionRow.maxMarks);
    if (Number.isFinite(directMax) && directMax > 0) return directMax;
    const directMarks = Number(questionRow.marks);
    if (Number.isFinite(directMarks) && directMarks > 0) return directMarks;
    return 1;
  };

  const getQuestionMaxMark = (questionId: string): number => {
    const q = questions.find((qq: any) => String(qq._id) === String(questionId));
    return getQuestionMaxMarkForQuestion(q);
  };

  useEffect(() => {
    if (questionBankList.length === 0 || questions.length === 0) return;
    if (hasMergedBankRef.current) return;
    hasMergedBankRef.current = true;

    setPcs((prev) =>
      prev.map((pc) => {
        const entry = questionBankList.find((b: any) => String(getPcIdFromEntry(b)) === String(pc._id));

        if (!entry?.questions?.length) return pc;
        const qList: Question[] = (entry.questions || []).map((q: any) => {
          const qId = q.questionId != null ? String(q.questionId) : '';
          const fullQuestion = questions.find((qq: any) => String(qq._id) === qId);
          const mergedMarks = fullQuestion
            ? getQuestionMaxMarkForQuestion(fullQuestion)
            : Math.max(Number(q.marks) || 0, 1);
          return {
            id: qId,
            text: fullQuestion?.question ?? '',
            marks: mergedMarks,
            questionType: fullQuestion?.questionType
          };
        });
        return { ...pc, questions: qList };
      })
    );
  }, [questionBankList, questions, rubricMaxMarkByRubricId]);

  const openAddQuestionDialog = (pcIndex: number) => {
    if (performanceEvalLocked) return;
    setCurrentPcIndex(pcIndex);
    setOpen(true);
  };

  const isQuestionAdded = (questionId: string, pcIndex: number) => pcs[pcIndex]?.questions.some((q) => q.id === questionId);

  const handleToggleQuestion = (selectedQuestion: any, pcIndex: number) => {
    if (performanceEvalLocked) return;
    const newPcs = [...pcs];
    const targetPc = newPcs[pcIndex];

    if (!targetPc) return;

    // If already added, simply remove
    if (isQuestionAdded(selectedQuestion._id, pcIndex)) {
      targetPc.questions = targetPc.questions.filter((q) => q.id !== selectedQuestion._id);
      setPcs(newPcs);
      return;
    }

    // About to add: ensure we do not exceed PC total marks
    const questionMarks = getQuestionMaxMarkForQuestion(selectedQuestion);
    const currentTotal = (targetPc.questions || []).reduce((sum: number, q: any) => sum + (Number(q.marks) || 0), 0);
    const pcMaxMarks = Number(targetPc.totalMarks) || 0;

    if (pcMaxMarks > 0 && currentTotal + questionMarks > pcMaxMarks) {
      Snakbar(`Cannot add this question. Selected marks (${currentTotal + questionMarks}) exceed PC total marks (${pcMaxMarks}).`, 'error');
      return;
    }

    const newQuestion: Question = {
      id: selectedQuestion._id,
      text: selectedQuestion.question,
      marks: questionMarks,
      questionType: selectedQuestion.questionType
    };
    targetPc.questions = [...targetPc.questions, newQuestion];
    setPcs(newPcs);
  };

  const deleteQuestion = (pcIndex: number, qIndex: number) => {
    if (performanceEvalLocked) return;
    const newPcs = [...pcs];
    newPcs[pcIndex].questions = newPcs[pcIndex].questions.filter((_, i) => i !== qIndex);
    setPcs(newPcs);
  };

  const existingPcIds = questionBankList.map(getPcIdFromEntry);
  const clearedPCs = pcs.filter((pc) => pc.questions.length === 0 && existingPcIds.some((id: any) => String(id) === String(pc._id)));
  const hasSomethingToSave = pcs.some((pc) => pc.questions.length > 0) || clearedPCs.length > 0;

  const handleCreate = async () => {
    if (performanceEvalLocked) {
      Snakbar('Performance evaluation is complete for this session. Assessment questions cannot be changed.', 'error');
      return;
    }
    const pcsWithQuestions = pcs.filter((pc) => pc.questions.length > 0);
    const pcsToSave = [...pcsWithQuestions, ...clearedPCs];

    if (pcsToSave.length === 0) {
      Snakbar('Please add at least one question to at least one PC.', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = pcsToSave.map((pc) => ({
        // assessmentTemplateId: assessment._id,
        assessmentSessionId: assessment._id,
        pcId: pc._id,
        // Use rubric's maxMark as total mark for each question when adding to question bank
        questions: pc.questions.map((q) => ({
          questionId: q.id,
          marks: Number(getQuestionMaxMark(q.id) ?? q.marks ?? 1)
        })),
        batchId: assessment.batch._id,
        templateName: assessment.templateName,
        startDate: assessment.startDate,
        endDate: assessment.endDate,
        isActive: true
      }));

      await bulkUpsert(payload);

      Snakbar('Assessment questions saved successfully.', 'success');

      navigate('/assessment-details');
    } catch (err: any) {
      console.error('Failed to save assessment questions:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const tableColumns = [
    {
      id: 'select',
      header: 'Added',
      cell: ({ row }: any) => {
        const added = isQuestionAdded(row.original._id, currentPcIndex);
        return (
          <Tooltip
            title={
              performanceEvalLocked
                ? 'Performance evaluation is complete; questions cannot be changed'
                : added
                  ? 'Click to remove from this PC'
                  : 'Click to add'
            }
            arrow
          >
            <span>
              <Checkbox
                checked={added}
                disabled={performanceEvalLocked}
                onChange={() => handleToggleQuestion(row.original, currentPcIndex)}
                color="primary"
                size="small"
              />
            </span>
          </Tooltip>
        );
      }
    },
    { id: 'question', header: 'Question', accessorKey: 'question' },
    {
      id: 'marks',
      header: 'Marks',
      cell: ({ row }: any) => (
        <Typography variant="body2" fontWeight={600}>
          {getQuestionMaxMarkForQuestion(row.original)}
        </Typography>
      )
    },
    {
      id: 'questionType',
      header: 'Type',
      accessorKey: 'questionType',
      cell: ({ getValue }: any) => {
        const raw = getValue?.();
        const label = getPcTypeLabel(raw) || '-';
        return <Chip label={label} size="small" color="primary" variant="outlined" />;
      }
    },
    {
      id: 'type',
      header: 'Sub Type',
      accessorKey: 'type',
      cell: ({ getValue }: any) => {
        const raw = getValue?.();
        const base = getPcTypeLabel(raw) || '-';
        return <Chip label={base.toUpperCase()} size="small" color="secondary" />;
      }
    }
  ];

  const infoItems = [
    {
      label: 'Qualification Pack',
      value: assessment.qualificationPack.qpName || '-',
      color: theme.palette.primary.main,
      icon: <Book size={18} />
    },
    {
      label: 'Batch',
      value: assessment.batch.name || '-',
      color: theme.palette.success.main,
      icon: <NoteText size={18} />
    },
    {
      label: 'Start Date',
      value: assessment.startDate ? new Date(assessment.startDate).toLocaleDateString() : '-',
      color: theme.palette.warning.main,
      icon: <Book size={18} />
    },
    {
      label: 'End Date',
      value: assessment.endDate ? new Date(assessment.endDate).toLocaleDateString() : '-',
      color: theme.palette.error.main,
      icon: <NoteText size={18} />
    }
  ];

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      {/* ── Header card with info strip ── */}
      <MainCard
        title={
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ width: '100%' }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <BackToggle show />
              <Box>
                <Typography variant="h5" fontWeight={700}>
                  {assessment.templateName || 'Assessment Questions'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Assign questions to each performance criteria
                </Typography>
              </Box>
            </Stack>
          </Stack>
        }
        content={false}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
            borderTop: '1px solid',
            borderColor: 'divider'
          }}
        >
          {infoItems.map((item, idx) => (
            <Box
              key={idx}
              sx={{
                px: 3,
                py: 2.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                borderRight: idx < infoItems.length - 1 ? '1px solid' : 'none',
                borderColor: 'divider'
              }}
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: '10px',
                  bgcolor: alpha(item.color, 0.1),
                  color: item.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                {item.icon}
              </Box>
              <Box minWidth={0}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}
                >
                  {item.label}
                </Typography>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {item.value}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </MainCard>

      {performanceEvalLocked && (
        <Alert severity="info">
          Performance evaluation for this session is complete. You can review assigned questions; adding, removing, or saving changes is
          disabled.
        </Alert>
      )}

      {displayNosList.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, borderRadius: 2, overflow: 'hidden', boxShadow: 1 }}>
          {displayNosList.map((nos, nosIdx) => {
            const nosPcIds = Array.isArray((nos as any)?.pcIds) ? (nos as any).pcIds.map(String) : [];
            const nosPcs =
              nosPcIds.length > 0
                ? pcs.filter((pc: any) => nosPcIds.includes(String(pc._id)))
                : pcs.filter((pc: any) => String(pc.nosId) === String(nos._id));

            return (
              <Accordion
                key={nos._id}
                defaultExpanded={nosIdx === 0}
                sx={{
                  '&:before': { display: 'none' },
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '0 !important',
                  '&:first-of-type': { borderTopLeftRadius: '8px !important', borderTopRightRadius: '8px !important' },
                  '&:last-of-type': { borderBottomLeftRadius: '8px !important', borderBottomRightRadius: '8px !important' },
                  '& + .MuiAccordion-root': { borderTop: 0 }
                }}
              >
                <AccordionSummary
                  sx={{
                    bgcolor: 'primary.lighter',
                    px: 2,
                    minHeight: 56,
                    '&.Mui-expanded': { minHeight: 56 }
                  }}
                >
                  <Box sx={{ width: '100%', pr: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                        <Typography variant="body2" fontWeight={700} color="text.secondary" sx={{ letterSpacing: 0.2, fontSize: '0.9rem' }}>
                          NOS:
                        </Typography>
                        <Chip label={nos.nosCode} size="small" color="primary" sx={{ fontWeight: 500, fontSize: '0.75rem', height: 22 }} />
                        <Typography variant="body2" fontWeight={600} color="primary.main" sx={{ fontSize: '0.95rem', ml: 0.5 }}>
                          {nos.nosName}
                        </Typography>
                      </Stack>
                    </Box>
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ p: 0, bgcolor: 'background.paper' }}>
                  <Divider />
                  <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {nosPcs.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                        No performance criteria linked to this NOS.
                      </Typography>
                    ) : (
                      nosPcs.map((pc, pcIdx) => {
                        const pcIndex = pcs.findIndex((p) => p._id === pc._id);
                        const pcMainType = getPcMainType(pc.type);
                        const pcTypeLabel = getPcTypeLabel(pc.type);
                        const typeColor = pcMainType === 'theory' ? theme.palette.primary.main : theme.palette.success.main;
                        const pcAllocatedMarks = (pc.questions || []).reduce((sum: number, q: any) => sum + (Number(q.marks) || 0), 0);
                        const pcMaxMarks = Number(pc.totalMarks) || 0;
                        const pcPercent = pcMaxMarks > 0 ? Math.round((pcAllocatedMarks / pcMaxMarks) * 100) : 0;

                        return (
                          <Accordion
                            key={pc._id}
                            defaultExpanded={pcIdx === 0}
                            sx={{
                              '&:before': { display: 'none' },
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: '8px !important',
                              overflow: 'hidden'
                            }}
                          >
                            <AccordionSummary sx={{ minHeight: 48, '&.Mui-expanded': { minHeight: 48 }, bgcolor: 'grey.50' }}>
                              <Box sx={{ width: '100%' }}>
                                <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
                                  {/* Left Side - PC Details */}
                                  <Box minWidth={0} flex={1}>
                                    <Typography variant="subtitle2" fontWeight={700} noWrap>
                                      {pc.pcCode} — {pc.pcName}
                                    </Typography>

                                    <Box mt={0.5}>
                                      <Chip
                                        size="small"
                                        variant="outlined"
                                        sx={{ height: 20, fontWeight: 500, fontSize: '0.78rem' }}
                                        label={
                                          <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Typography
                                              component="span"
                                              sx={{ textTransform: 'capitalize', fontWeight: 500, fontSize: '0.78rem' }}
                                            >
                                              {getPcTypeLabel(pc.type)}
                                            </Typography>
                                            <Typography
                                              component="span"
                                              sx={{
                                                color: pc.isCritical ? 'error.main' : 'text.secondary',
                                                fontWeight: 500,
                                                ml: 0.5,
                                                textTransform: 'none',
                                                fontSize: '0.78rem'
                                              }}
                                            >
                                              - {pc.isCritical ? 'Critical' : 'Non-critical'}
                                            </Typography>
                                          </Box>
                                        }
                                      />
                                    </Box>
                                  </Box>

                                  {/* Right Side - PC Marks Progress */}
                                  <Box sx={{ width: 180, textAlign: 'right', flexShrink: 0 }}>
                                    <Typography variant="caption" fontWeight={700}>
                                      {pcAllocatedMarks} / {pcMaxMarks || 0}
                                    </Typography>
                                    <LinearProgress
                                      variant="determinate"
                                      value={Math.min(pcPercent, 100)}
                                      color={pcPercent >= 80 ? 'success' : pcPercent >= 50 ? 'warning' : 'primary'}
                                      sx={{ height: 5, borderRadius: 1, mt: 0.4 }}
                                    />
                                  </Box>
                                </Stack>
                              </Box>
                            </AccordionSummary>

                            <AccordionDetails sx={{ p: 0 }}>
                              <Divider />

                              {/* Add Question button */}
                              <Box sx={{ px: 2, pt: 1.5, pb: 1, display: 'flex', justifyContent: 'flex-end' }}>
                                <Tooltip
                                  title={
                                    performanceEvalLocked
                                      ? 'Performance evaluation is complete; questions cannot be changed'
                                      : 'Add questions from the bank'
                                  }
                                >
                                  <span>
                                    <Button
                                      variant="contained"
                                      size="small"
                                      disableElevation
                                      disabled={performanceEvalLocked}
                                      startIcon={<Add size={15} />}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openAddQuestionDialog(pcIndex);
                                      }}
                                      sx={{ borderRadius: 1.5 }}
                                    >
                                      Add Question
                                    </Button>
                                  </span>
                                </Tooltip>
                              </Box>

                              {/* Column header */}
                              <Box
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: '40px 1fr 100px 44px',
                                  px: 3,
                                  py: 1,
                                  bgcolor: alpha(theme.palette.grey[500], 0.06)
                                }}
                              >
                                {['#', 'QUESTION', 'TYPE', ''].map((h, i) => (
                                  <Typography key={i} variant="caption" fontWeight={700} color="text.secondary">
                                    {h}
                                  </Typography>
                                ))}
                              </Box>

                              {/* Questions list */}
                              {pc.questions.length === 0 ? (
                                <Box py={4} textAlign="center">
                                  <NoteText size={32} color={theme.palette.text.disabled} style={{ marginBottom: 6 }} />
                                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                    No questions added yet
                                  </Typography>
                                  <Typography variant="caption" color="text.disabled">
                                    Click "Add Question" above to get started
                                  </Typography>
                                </Box>
                              ) : (
                                <>
                                  <Box
                                    sx={{
                                      maxHeight: 240,
                                      overflowY: 'auto',
                                      overflowX: 'hidden',
                                      '&::-webkit-scrollbar': { width: 6 },
                                      '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                                      '&::-webkit-scrollbar-thumb': {
                                        bgcolor: alpha(theme.palette.grey[500], 0.35),
                                        borderRadius: 3,
                                        '&:hover': { bgcolor: alpha(theme.palette.grey[500], 0.6) }
                                      }
                                    }}
                                  >
                                    {pc.questions.map((q, qIndex) => (
                                      <Box
                                        key={q.id || qIndex}
                                        sx={{
                                          display: 'grid',
                                          gridTemplateColumns: '40px 1fr 100px 44px',
                                          alignItems: 'center',
                                          px: 3,
                                          py: 1.1,
                                          borderTop: '1px solid',
                                          borderColor: 'divider',
                                          transition: 'background 0.15s',
                                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) }
                                        }}
                                      >
                                        <Typography variant="body2" color="text.disabled" fontWeight={600}>
                                          {qIndex + 1}.
                                        </Typography>
                                        <Typography variant="body2" noWrap pr={2} title={q.text}>
                                          {q.text}
                                        </Typography>
                                        <Box display="flex" justifyContent="flex-start">
                                          <Chip
                                            label={q.questionType ? q.questionType.charAt(0).toUpperCase() + q.questionType.slice(1) : '-'}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                          />
                                        </Box>
                                        <Box display="flex" justifyContent="center">
                                          <Tooltip
                                            title={
                                              performanceEvalLocked
                                                ? 'Performance evaluation is complete; questions cannot be changed'
                                                : 'Remove'
                                            }
                                          >
                                            <span>
                                              <IconButton
                                                size="small"
                                                color="error"
                                                disabled={performanceEvalLocked}
                                                onClick={() => deleteQuestion(pcIndex, qIndex)}
                                              >
                                                <Bag />
                                              </IconButton>
                                            </span>
                                          </Tooltip>
                                        </Box>
                                      </Box>
                                    ))}
                                  </Box>
                                  {/* Footer removed: questions count and total marks */}
                                </>
                              )}
                            </AccordionDetails>
                          </Accordion>
                        );
                      })
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      ) : (
        /* Fallback: flat PC list if no NOS structure */
        <Stack spacing={2}>
          {pcs.map((pc, pcIndex) => {
            const pcMainType = getPcMainType(pc.type);
            const pcTypeLabel = getPcTypeLabel(pc.type);
            const typeColor = pcMainType === 'theory' ? theme.palette.primary.main : theme.palette.success.main;

            return (
              <Paper
                key={pc._id}
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden'
                }}
              >
                {/* PC header row */}
                <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                    <Box minWidth={0} flex={1}>
                      <Stack direction="row" alignItems="center" spacing={0.75} mb={0.75} flexWrap="wrap">
                        <Chip
                          label={pc.pcCode}
                          size="small"
                          sx={{
                            height: 22,
                            fontWeight: 700,
                            fontSize: '0.78rem',
                            bgcolor: alpha(typeColor, 0.1),
                            color: typeColor,
                            border: 'none'
                          }}
                        />
                        <Chip
                          label={pcTypeLabel ? pcTypeLabel.charAt(0).toUpperCase() + pcTypeLabel.slice(1) : '-'}
                          size="small"
                          color={pcMainType === 'theory' ? 'primary' : 'success'}
                          variant="outlined"
                        />
                        <Chip
                          label={pc.isCritical ? 'Critical' : 'Non-critical'}
                          size="small"
                          color={pc.isCritical ? 'error' : 'default'}
                          variant="outlined"
                        />
                      </Stack>
                      <Typography variant="subtitle1" fontWeight={500} sx={{ color: 'common.black', fontSize: '0.95rem' }}>
                        {pc.pcName}
                      </Typography>
                    </Box>

                    <Tooltip
                      title={
                        performanceEvalLocked
                          ? 'Performance evaluation is complete; questions cannot be changed'
                          : 'Add questions from the bank'
                      }
                    >
                      <span>
                        <Button
                          variant="contained"
                          size="small"
                          disableElevation
                          disabled={performanceEvalLocked}
                          startIcon={<Add size={15} />}
                          onClick={() => openAddQuestionDialog(pcIndex)}
                          sx={{ borderRadius: 1.5, flexShrink: 0, mt: 0.5 }}
                        >
                          Add Question
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>
                </Box>

                <Divider />

                {/* Column header */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '40px 1fr 80px 80px 44px',
                    px: 3,
                    py: 1,
                    bgcolor: alpha(theme.palette.grey[500], 0.06)
                  }}
                >
                  {['#', 'QUESTION', 'TYPE', 'MARKS', ''].map((h, i) => (
                    <Typography key={i} variant="caption" fontWeight={700} color="text.secondary">
                      {h}
                    </Typography>
                  ))}
                </Box>

                {/* Questions list */}
                {pc.questions.length === 0 ? (
                  <Box py={5} textAlign="center">
                    <NoteText size={36} color={theme.palette.text.disabled} style={{ marginBottom: 8 }} />
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      No questions added yet
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      Click "Add Question" above to get started
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {/* Scrollable rows — capped at 5 rows (~240 px) */}
                    <Box
                      sx={{
                        maxHeight: 240,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        '&::-webkit-scrollbar': { width: 6 },
                        '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                        '&::-webkit-scrollbar-thumb': {
                          bgcolor: alpha(theme.palette.grey[500], 0.35),
                          borderRadius: 3,
                          '&:hover': { bgcolor: alpha(theme.palette.grey[500], 0.6) }
                        }
                      }}
                    >
                      {pc.questions.map((q, qIndex) => (
                        <Box
                          key={q.id || qIndex}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '40px 1fr 80px 80px 44px',
                            alignItems: 'center',
                            px: 3,
                            py: 1.1,
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            transition: 'background 0.15s',
                            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) }
                          }}
                        >
                          <Typography variant="body2" color="text.disabled" fontWeight={600}>
                            {qIndex + 1}.
                          </Typography>
                          <Typography variant="body2" noWrap pr={2} title={q.text}>
                            {q.text}
                          </Typography>
                          <Typography variant="body2" textAlign="left" fontWeight={500}>
                            {q.questionType ? q.questionType.charAt(0).toUpperCase() + q.questionType.slice(1) : '-'}
                          </Typography>
                          <Typography variant="body2" textAlign="right" fontWeight={600}>
                            {Number(q.marks) || 0}
                          </Typography>
                          <Box display="flex" justifyContent="center">
                            <Tooltip
                              title={
                                performanceEvalLocked
                                  ? 'Performance evaluation is complete; questions cannot be changed'
                                  : 'Remove'
                              }
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={performanceEvalLocked}
                                  onClick={() => deleteQuestion(pcIndex, qIndex)}
                                >
                                  <Bag />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </Box>
                      ))}
                    </Box>

                    {/* Footer removed: questions count and total marks */}
                  </>
                )}
              </Paper>
            );
          })}
        </Stack>
      )}

      {/* ── Save button ── */}
      <Box display="flex" justifyContent="flex-end" pb={1}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          disableElevation
          disabled={isSubmitting || !hasSomethingToSave || performanceEvalLocked}
          onClick={handleCreate}
          sx={{ minWidth: 200, borderRadius: 2, py: 1.2, fontWeight: 700 }}
        >
          {isSubmitting ? 'Saving…' : 'Save Assessment'}
        </Button>
      </Box>

      {/* ── Question selection dialog ── */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Select Questions
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                {pcs[currentPcIndex]?.pcCode} — {pcs[currentPcIndex]?.pcName}
              </Typography>
            </Box>
            <Chip
              label={`${pcs[currentPcIndex]?.questions?.length ?? 0} selected`}
              color="primary"
              size="small"
              sx={{ fontWeight: 600, mt: 0.5 }}
            />
          </Stack>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2 }}>
          {(() => {
            const currentPc = pcs[currentPcIndex];
            const allowedTypes = getAllowedQuestionTypesForPc(currentPc?.type);
            const currentPcDisplayType = getPcTypeLabel(currentPc?.type) || allowedTypes.join(' / ') || '';
            const pcMaxMarks = Number(currentPc?.totalMarks) || 0;
            const pcAllocatedMarks = (currentPc?.questions || []).reduce((sum: number, q: any) => sum + (Number(q.marks) || 0), 0);
            const pcPercent = pcMaxMarks > 0 ? Math.round((pcAllocatedMarks / pcMaxMarks) * 100) : 0;

            if (questionsLoading) {
              return (
                <Box py={6} textAlign="center">
                  <Typography color="text.secondary">Loading questions…</Typography>
                </Box>
              );
            }

            const groups: { key: 'question' | 'task' | 'viva'; label: string }[] = [
              {
                key: 'question',
                label: 'Theory Questions'
              },
              {
                key: 'task',
                label: 'Task Questions'
              },
              {
                key: 'viva',
                label: 'Viva Questions'
              }
            ];

            const anyVisibleRows = groups.some((g) => {
              if (!allowedTypes.includes(g.key)) return false;
              return questions.some((q) => (q.questionType || '').toLowerCase() === g.key);
            });

            if (!anyVisibleRows) {
              return (
                <Box py={7} textAlign="center" display="flex" flexDirection="column" alignItems="center" gap={1.5}>
                  <NoteText size={40} color={theme.palette.text.disabled} />
                  <Box>
                    <Typography variant="body1" fontWeight={600} color="text.secondary">
                      No {currentPcDisplayType || 'matching'} questions in the bank
                    </Typography>
                    <Typography variant="body2" color="text.disabled" mt={0.5}>
                      Add appropriate type questions to the question bank, then come back to assign them here.
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Add size={15} />}
                    onClick={() => navigate('/question-bank/add')}
                    sx={{ mt: 0.5, borderRadius: 1.5 }}
                  >
                    Add {currentPcDisplayType || 'New'} Question
                  </Button>
                </Box>
              );
            }

            return (
              <Stack spacing={3}>
                {/* PC marks progress inside dialog */}
                <Box>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                    Marks selected for this PC: {pcAllocatedMarks} / {pcMaxMarks || 0}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(pcPercent, 100)}
                    color={pcPercent >= 80 ? 'success' : pcPercent >= 50 ? 'warning' : 'primary'}
                    sx={{ height: 6, borderRadius: 1 }}
                  />
                </Box>
                {groups.map((group) => {
                  if (!allowedTypes.includes(group.key)) return null;
                  const data = questions.filter((q) => (q.questionType || '').toLowerCase() === group.key);
                  if (!data.length) return null;

                  return (
                    <Box key={group.key}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                        {group.label}
                      </Typography>
                      <CustomTable columns={tableColumns} data={data} striped />
                    </Box>
                  );
                })}
              </Stack>
            );
          })()}
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            {pcs[currentPcIndex]?.questions?.length ?? 0} question{(pcs[currentPcIndex]?.questions?.length ?? 0) !== 1 ? 's' : ''} selected
          </Typography>
          <Box>
            <Button onClick={() => setOpen(false)} variant="outlined" sx={{ borderRadius: 1.5, mr: 1 }}>
              Cancel
            </Button>
            <Button onClick={() => setOpen(false)} variant="contained" disableElevation sx={{ borderRadius: 1.5, px: 3 }}>
              Done
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssessmentQuestions;
